import React, { useEffect, useRef, useState } from 'react';
import MemberInfoCard from '../../components/Members/MemberInfoCard';
import { getMember, getMemberMedicalCard, getOnboardingLink, getPreScanQuestionnaireLink, repeatBloodTestBooking, sendDoctorLink, sendMRIScanReviewInvitation, sendNutritionistLink, sendPreScanConsultationInvitation, sendPreScanQuestionnaire } from '../../api/members';
import './MemberPage.scss';
import { useLocation } from 'react-router';
import ViewNoteModal from '../../components/Alerts/ViewNoteModal';
import { ReactComponent as ArrowRightIcon } from '../../assets/icons/arrow-right.svg';
import { toggleSidebarCollapsed } from '../../store/pageSlice';
import {
  changeAddressModalVisibility,
  changeAssignDoctorModalVisibility,
  changeAssignedDoctorId,
  changeAssignedNutritionistId,
  changeAssignNutritionistModalVisibility,
  changeAssignedInchargeAdminId,
  changeAssignInchargeModalVisibility,
  changeMedicalCardModalVisibility,
  changeNotesModalVisibility,
  setMemberData,
  setMemberMedicalCard
} from '../../store/memberSlice';
import { useDispatch, useSelector } from 'react-redux';
import Appointments from '../../components/Members/Appointments';
import HealthSummaryTab from '../../components/Members/HealthSummary';
import TestResults from '../../components/Members/TestResults';
import Summary from '../../components/Members/SummaryTab';
import Loader from '../../components/BaseComponents/Loader';
import DietDiary from '../../components/Members/DietDiary';
import { RootState } from '../../store/store';
import { UserRoleEnum } from '../../types/apiGeneratedTypes';
import AssignDoctorModal from '../../components/Members/MemberInfoCard/AssignDoctorModal';
import MemberMedicalCardInfoModal from '../../components/Members/MedicalCardModal';
import ChangeAddressModal from '../../components/Members/MemberInfoCard/ChangeAddressModal';
import Recommendations from '../../components/Members/Recommendations';
import AssignNutritionistModal from '../../components/Members/MemberInfoCard/AssignNutritionistModal';
import AssignInchargeModal from '../../components/Members/MemberInfoCard/AssignInchargeModal';
import HL7Listing from '../../components/HL7Listing';
import BiologicalAgeListing from '../../components/BiologicalAgeListing';
import Supplements from '../../components/Supplements';
import OldBloodTest from '../../components/OldBloodTest';
import DexaReports from '../../components/DexaReports';
import Notes from '../../components/Notes';
import MemberNotesModal from '../../components/Members/MemberInfoCard/MemberNotes/MemberNotesModal';
import AuditLogs from '../../components/AuditLogs';
import FullBodyScan from '../../components/FullBodyScan';
import BodySystemAnalysisTab from '../../components/Members/BodySystemAnalysisTab';
import FilesTab from '../../components/Members/Files/FilesTab';
import ScanQuestionnaires from '../../components/ScanQuestionnaires';
import OnboardingFormData from '../../components/OnboardingFormData';
import MemberRestrictedChat from '../../components/Members/MemberRestrictedChat';
import PersonalisedMonitoringPlanTab from '../../components/Members/PersonalisedMonitoringPlanTab';
import { readStaffNotification } from '../../api/staff';
import LinkIcon from '../../assets/icons/link-icon.svg';
import { resentTuliBooking } from '../../api/pharma';
import { toast } from 'react-toastify';
import CalendarIcon from '../../assets/icons/Calendar-0.svg';

type CalendlyLinkType = 'nutritionist' | 'gp' | 'mri';
const CALENDLY_LINKS: Record<CalendlyLinkType, string> = {
  nutritionist: '<NUTRITIONIST_CALENDLY_LINK>',
  gp: '<GP_CALENDLY_LINK>',
  mri: '<MRI_CALENDLY_LINK>'
};

const MemberPage = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<any | null>(null);
  const [activeTab, setActiveTab] = useState<
    | 'appointments'
    | 'results'
    | 'summary'
    | 'recommendations'
    | 'diet'
    | 'health'
    | 'tests'
    | 'hl7'
    | 'biologicalAge'
    | 'supplements'
    | 'oldBloodTests'
    | 'dexaReports'
    | 'notes'
    | 'bodySystemAnalysis'
    | 'fullBodyScan'
    | 'auditLogs'
    | 'files'
    | 'scanQuestionnaires'
    | 'onboardingForm'
    | 'action'
    | 'restrictedChat'
    | 'personalisedMonitoringPlan'
  >('appointments');
  const [isViewNoteModalOpen, setIsViewNoteModalOpen] = useState<boolean>(false);
  const [noteData, setNoteData] = useState<any>(null);
  const [openMenu, setOpenMenu] = useState<null | 'results' | 'nutrition' | 'filesData' | 'notes' | 'action'>(
    null
  );
  const [noteTab, setNoteTab] = useState<'notes' | 'followOnAlerts' | 'referralLetter'>('notes');
  const [openSubMenu, setOpenSubMenu] = useState<
    null | 'scan' | 'blood' | 'gp' | 'misc'
  >(null);

  const toggleSubMenu = (section: 'scan' | 'blood' | 'gp' | 'misc') => {
    setOpenSubMenu((current) => (current === section ? null : section));
  };
  const {
    isAssignDoctorModalOpened,
    isMedicalCardModalOpened,
    isAddressModalOpened,
    isAssignNutritionistModalOpened,
    isAssignInchargeModalOpened,
    isNotesModalOpened,
    notesModalIntent,
    notesModalForceViewOnly,
    notesModalIsReferralLetter,
    memberData,
    medicalCard
  } = useSelector((state: RootState) => state.member);
  const { sidebarCollapsed } = useSelector((state: RootState) => state.page);
  const { admin: { role } } = useSelector((state: RootState) => state.admin);
  const canUseRestrictedChat =
    role === UserRoleEnum.Admin || role === UserRoleEnum.SuperAdmin;
  const navRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const membershipType = memberData?.membership_type?.toLowerCase();
  const isPlusOrScanMember = membershipType === 'plus' || membershipType === 'scan';

  const isResultsTabActive =
    activeTab === 'tests' ||
    activeTab === 'fullBodyScan' ||
    activeTab === 'bodySystemAnalysis' ||
    activeTab === 'biologicalAge' ||
    activeTab === 'personalisedMonitoringPlan';
  const isNutritionTabActive = activeTab === 'recommendations' || activeTab === 'supplements';
  const isFilesDataTabActive =
    activeTab === 'files' ||
    activeTab === 'hl7' ||
    activeTab === 'oldBloodTests' ||
    activeTab === 'dexaReports' ||
    activeTab === 'scanQuestionnaires' ||
    activeTab === 'onboardingForm' ||
    activeTab === 'auditLogs';
  const isScanMember = membershipType === 'scan';

  const selectTab = (tab: typeof activeTab) => {
    setActiveTab(tab);
    setOpenMenu(null);
  };
  const hasCompletedOnboarding =
    medicalCard && Object.values(medicalCard).some((value) => value !== null);

  useEffect(() => {
    const fetchMember = async () => {
      try {
        setIsLoading(true);
        const memberId = location.pathname.split('/')[2];
        const member = await getMember(memberId);
        const memberMedicalCard = await getMemberMedicalCard(memberId);
        dispatch(setMemberData(member));
        dispatch(setMemberMedicalCard(memberMedicalCard));
      } catch (error) {
        setError(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMember();
  }, [dispatch, location.pathname]);

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (!navRef.current) return;
      if (e.target instanceof Node && !navRef.current.contains(e.target)) {
        setOpenMenu(null);
        setOpenSubMenu(null);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpenMenu(null);
        setOpenSubMenu(null);
      }
    };

    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  useEffect(() => {
    if (location.state?.openNoteModal && location.state?.noteData) {
      setNoteData(location.state.noteData);
      setIsViewNoteModalOpen(true);
      window.history.replaceState({}, document.title);
    }

    // Handle activeTab from location state
    if (location.state?.activeTab) {
      const requestedTab = location.state.activeTab;
      if (requestedTab === 'restrictedChat' && !canUseRestrictedChat) {
        setActiveTab('appointments');
      } else {
        setActiveTab(requestedTab);
      }
    }

    // Handle activeSubTab from location state
    if (location.state?.activeSubTab) {
      // This will be handled by the Notes component
    }
  }, [location.state, canUseRestrictedChat]);

  useEffect(() => {
    if (!canUseRestrictedChat && activeTab === 'restrictedChat') {
      setActiveTab('appointments');
    }
  }, [canUseRestrictedChat, activeTab]);

  const onMarkUnread = async () => {
    if (!location.state?.openNoteModal || !location.state?.noteData) return;
    const alertId = location.state.alertId;
    try {
      await readStaffNotification(alertId, false);
      setIsViewNoteModalOpen(false);
      setNoteData(null);
    } catch {
    }
  }

  const sentTuliBooking = async (memberId: string) => {
    try {
      const response = await resentTuliBooking(memberId);
      if (response.status === 'success') {
        toast.success('Tuli booking requested successfully');
      } else {
        toast.error('Failed to request Tuli booking');
      }
    } catch (error) {
      toast.error(error?.toString());
      // toast.error('Failed to request Tuli booking');
    }
  };

  const sendRepeatBloodTestBooking = async (memberId: string) => {
    try {
      const response = await repeatBloodTestBooking(memberId);
      if (response.status === 'success') {
        toast.success('Repeat blood test booking requested successfully!');
      } else {
        toast.error('Failed to request repeat blood test booking.');
      }
    } catch (error) {
      toast.error(error?.toString());
    }
  };

  const sendDoctorBooking = async (memberId: string) => {
    try {
      const response = await sendDoctorLink(memberId);
      if (response.status === 'success') {
        toast.success('Email sent to specialist successfully!');
      } else {
        toast.error('Failed to send email to specialist.');
      }
    } catch (error) {
      toast.error(error?.toString());
    }
  };

  const sendPreScanConsultationInvitationLink = async (memberId: string) => {
    try {
      const response = await sendPreScanConsultationInvitation(memberId);
      if (response.status === 'success') {
        toast.success('Pre-scan Consultation Invitation sent successfully!');
      } else {
        toast.error('Failed to send Pre-scan Consultation Invitation.');
      }
    } catch (error) {
      toast.error(error?.toString());
    }
  };

  const sendMRIScanReviewInvitationLink = async (memberId: string) => {
    try {
      const response = await sendMRIScanReviewInvitation(memberId);
      if (response.status === 'success') {
        toast.success('MRI Scan Review Invitation sent successfully!');
      } else {
        toast.error('Failed to send MRI Scan Review Invitation.');
      }
    } catch (error) {
      toast.error(error?.toString());
    }
  };

  const sendNutritionistBooking = async (memberId: string) => {
    try {
      const response = await sendNutritionistLink(memberId);
      if (response.status === 'success') {
        toast.success('Email sent to specialist successfully!');
      } else {
        toast.error('Failed to send email to specialist.');
      }
    } catch (error) {
      toast.error(error?.toString());
    }
  };

  const getCalendlyLinkForType = async (
    type: CalendlyLinkType,
    memberId: string
  ) => {
    if (type === 'nutritionist') {
      const response = await sendNutritionistLink(memberId);
      return response?.link || CALENDLY_LINKS.nutritionist;
    }

    if (type === 'gp') {
      const response = await sendDoctorLink(memberId);
      return response?.link || CALENDLY_LINKS.gp;
    }

    const response = await sendMRIScanReviewInvitation(memberId);
    return response?.link || CALENDLY_LINKS.mri;
  };

  const handleCopy = async (type: CalendlyLinkType) => {
    try {
      const memberId = memberData?.user_id;
      if (!memberId) {
        toast.error('Failed to copy link. Please try again.');
        return;
      }

      const link = await getCalendlyLinkForType(type, memberId);
      if (!link) {
        toast.error('Failed to copy link. Please try again.');
        return;
      }

      await navigator.clipboard.writeText(link);
      toast.success('Link copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy link. Please try again.');
    }
  };

  const copyOnboardingLink = async (memberId: string) => {
    try {
      const response = await getOnboardingLink(memberId);
      if (response.link) {
        await navigator.clipboard.writeText(response.link);
        toast.success('Onboarding link copied!');
      } else {
        toast.error('Unable to copy link');
      }
    } catch (error: any) {
      let errorMessage = 'Unable to copy link';

      if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.detail) {
        errorMessage = error.detail;
      }

      toast.error(errorMessage);
    }
  };

  const sendPreScanQuestionnaireLink = async (memberId: string) => {
    try {
      const response = await sendPreScanQuestionnaire(memberId);
      if (response.status === 'success') {
        toast.success('Pre-scan questionnaire sent successfully!');
      } else {
        toast.error('Failed to send pre-scan questionnaire.');
      }
    } catch (error: any) {
      let errorMessage = 'Failed to send pre-scan questionnaire.';
      if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    }
  };

  const copyPreScanQuestionnaireLink = async (memberId: string) => {
    try {
      const response = await getPreScanQuestionnaireLink(memberId);
      if (response.link) {
        await navigator.clipboard.writeText(response.link);
        toast.success('Pre-scan questionnaire link copied!');
      } else {
        toast.error('Unable to copy link');
      }
    } catch (error: any) {
      let errorMessage = 'Unable to copy link';

      if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.detail) {
        errorMessage = error.detail;
      }

      toast.error(errorMessage);
    }
  };

  if (isLoading) return <Loader />;
  if (error) return <div>Error loading member data</div>;

  return (
    <div className={`member-page-container${sidebarCollapsed ? ' collapsed' : ''}`}>
      <button
        type='button'
        className='sidebar-toggle-btn pos1'
        onClick={() => dispatch(toggleSidebarCollapsed())}
        aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <ArrowRightIcon />
      </button>
      <MemberInfoCard  sidebarCollapsed={sidebarCollapsed}/>
      <div className='tab-navigation-arrow-shell'>
        <div className='tab-navigation member-nav' ref={navRef}>
          <button
            className={activeTab === 'appointments' ? 'active' : ''}
            onClick={() => selectTab('appointments')}
            type='button'
          >
            Appointments
          </button>

          <div className='member-nav__dropdown'>
            <button
              className={activeTab === 'notes' ? 'active' : ''}
              onClick={() => setOpenMenu(openMenu === 'notes' ? null : 'notes')}
              aria-haspopup='menu'
              aria-expanded={openMenu === 'notes'}
              type='button'
            >
              Notes
              <span className='member-nav__caret' aria-hidden='true' />
            </button>
            {openMenu === 'notes' && (
              <div className='member-nav__menu' role='menu'>
                <button role='menuitem' type='button' onClick={() => { selectTab('notes'); setNoteTab('notes'); }}>
                  Notes
                </button>
                <button role='menuitem' type='button' onClick={() => { selectTab('notes'); setNoteTab('followOnAlerts'); }}>
                  Follow-on
                </button>
                <button role='menuitem' type='button' onClick={() => { selectTab('notes'); setNoteTab('referralLetter'); }}>
                  Referral letter
                </button>
              </div>
            )}
          </div>

          <div className='member-nav__dropdown'>
            <button
              className={isResultsTabActive ? 'active' : ''}
              onClick={() => setOpenMenu(openMenu === 'results' ? null : 'results')}
              aria-haspopup='menu'
              aria-expanded={openMenu === 'results'}
              type='button'
            >
              Results
              <span className='member-nav__caret' aria-hidden='true' />
            </button>
            {openMenu === 'results' && (
              <div className='member-nav__menu' role='menu'>
                <button role='menuitem' type='button' onClick={() => selectTab('tests')}>
                  Biomarkers
                </button>
                <button role='menuitem' type='button' onClick={() => selectTab('fullBodyScan')}>
                  Full-body Scan
                </button>
                <button role='menuitem' type='button' onClick={() => selectTab('bodySystemAnalysis')}>
                  Bodysystem Analysis
                </button>
                <button role='menuitem' type='button' onClick={() => selectTab('biologicalAge')}>
                  Biological Age
                </button>
                <button
                  role='menuitem'
                  type='button'
                  onClick={() => selectTab('personalisedMonitoringPlan')}
                >
                  Personalised monitoring plan
                </button>
              </div>
            )}
          </div>

          <div className='member-nav__dropdown'>
            <button
              className={isNutritionTabActive ? 'active' : ''}
              onClick={() => setOpenMenu(openMenu === 'nutrition' ? null : 'nutrition')}
              aria-haspopup='menu'
              aria-expanded={openMenu === 'nutrition'}
              type='button'
            >
              Nutrition
              <span className='member-nav__caret' aria-hidden='true' />
            </button>
            {openMenu === 'nutrition' && (
              <div className='member-nav__menu' role='menu'>
                <button role='menuitem' type='button' onClick={() => selectTab('recommendations')}>
                  Recommendations
                </button>
                <button role='menuitem' type='button' onClick={() => selectTab('supplements')}>
                  Supplements
                </button>
              </div>
            )}
          </div>

          <div className='member-nav__dropdown'>
            <button
              className={isFilesDataTabActive ? 'active' : ''}
              onClick={() => setOpenMenu(openMenu === 'filesData' ? null : 'filesData')}
              aria-haspopup='menu'
              aria-expanded={openMenu === 'filesData'}
              type='button'
            >
              Files &amp; Data
              <span className='member-nav__caret' aria-hidden='true' />
            </button>
            {openMenu === 'filesData' && (
              <div className='member-nav__menu' role='menu'>
                <button role='menuitem' type='button' onClick={() => selectTab('files')}>
                  Files
                </button>
                <button role='menuitem' type='button' onClick={() => selectTab('hl7')}>
                  HL7 Files
                </button>
                <button role='menuitem' type='button' onClick={() => selectTab('oldBloodTests')}>
                  Old Blood Tests
                </button>
                <button role='menuitem' type='button' onClick={() => selectTab('dexaReports')}>
                  DEXA Reports
                </button>
                <button role='menuitem' type='button' onClick={() => selectTab('scanQuestionnaires')}>
                  Scan Questionnaires
                </button>
                <button role='menuitem' type='button' onClick={() => selectTab('onboardingForm')}>
                  Onboarding Form
                </button>
                <button role='menuitem' type='button' onClick={() => selectTab('auditLogs')}>
                  Audit Logs
                </button>
              </div>
            )}
          </div>

          {canUseRestrictedChat && (
            <button
              className={activeTab === 'restrictedChat' ? 'active' : ''}
              onClick={() => selectTab('restrictedChat')}
              type='button'
            >
              Unrestricted Chat
            </button>
          )}

          <div className="member-nav__dropdown">
            <button
              className={activeTab === 'action' ? 'active' : ''}
              onClick={() => {
                if (openMenu === 'action') {
                  setOpenSubMenu(null);
                }
                setOpenMenu(openMenu === 'action' ? null : 'action');
              }}
              aria-haspopup="menu"
              aria-expanded={openMenu === "action"}
              type="button"
            >
              Actions
              <span className='member-nav__caret' aria-hidden='true' />
            </button>

            {openMenu === "action" && (
              <div className="member-nav__menu" role="menu">

                {/* Scan — Plus & Scan members only */}
                {isPlusOrScanMember && (
                  <div className="submenu-wrapper">
                    <button
                      type="button"
                      onClick={() => toggleSubMenu('scan')}
                    >
                      Scan ▸
                    </button>

                    {openSubMenu === 'scan' && (
                      <div className="submenu">
                        <button
                          onClick={() => {
                            sendPreScanQuestionnaireLink(memberData?.user_id!);
                          }}
                        ><img src={LinkIcon} alt='' /> &nbsp;
                          <span>Send Pre-Scan Questionnaire</span></button>
                        <button
                          onClick={() => {
                            copyPreScanQuestionnaireLink(memberData?.user_id!);
                          }}
                        ><img src={LinkIcon} alt='' /> &nbsp;
                          <span>Copy Pre-Scan Questionnaire</span></button>
                      </div>
                    )}
                  </div>
                )}

                {/* Blood */}
                <div className="submenu-wrapper">
                  <button
                    type="button"
                    onClick={() => toggleSubMenu('blood')}
                  >
                    Blood ▸
                  </button>

                  {openSubMenu === 'blood' && (
                    <div className="submenu">
                      {(hasCompletedOnboarding || (memberData as any)?.no_onboarding === true) && (
                        <button
                          onClick={() => {
                            sentTuliBooking(memberData?.user_id!);
                          }}
                        ><img src={CalendarIcon} alt='' /> &nbsp;
                          <span>Request booking via Tuli</span></button>
                      )}
                      <button
                        onClick={() => {
                          sendRepeatBloodTestBooking(memberData?.user_id!);
                        }}
                      > <img src={CalendarIcon} alt='' /> &nbsp;
                        <span>Repeat blood test</span></button>
                    </div>
                  )}
                </div>

                {/* GP & Nutritionist */}
                <div className="submenu-wrapper">
                  <button
                    type="button"
                    onClick={() => toggleSubMenu('gp')}
                  >
                    GP & Nutritionist ▸
                  </button>

                  {openSubMenu === 'gp' && (
                    <div className="submenu">
                      <button
                        onClick={() => {
                          sendNutritionistBooking(memberData?.user_id!);
                        }}
                      >
                        <img src={LinkIcon} alt='' /> &nbsp;
                        <span>Send Nutritionist Appointment Link</span>
                      </button>
                      <button
                        onClick={() => {
                          sendDoctorBooking(memberData?.user_id!);
                        }}
                      > <img src={LinkIcon} alt='' /> &nbsp;
                        <span>Send GP Appointment Link</span></button>
                      <button
                        onClick={() => {
                          sendPreScanConsultationInvitationLink(
                            memberData?.user_id!
                          );
                        }}
                      >
                        <img src={LinkIcon} alt='' /> &nbsp;
                        <span>Send Pre-scan consultation Invite</span>
                      </button>
                      {isPlusOrScanMember && (
                        <button
                          onClick={() => {
                            sendMRIScanReviewInvitationLink(memberData?.user_id!);
                          }}
                        >
                          <img src={LinkIcon} alt='' /> &nbsp;
                          <span>Send MRI Scan review Invite</span>
                        </button>)}
                      <button
                        onClick={() => {
                          handleCopy('nutritionist');
                        }}
                      >
                        <img src={LinkIcon} alt='' /> &nbsp;
                        <span>Copy Nutritionist Appointment Link</span>
                      </button>
                      <button
                        onClick={() => {
                          handleCopy('gp');
                        }}
                      >
                        <img src={LinkIcon} alt='' /> &nbsp;
                        <span>Copy GP Appointment Link</span>
                      </button>
                      {isScanMember && (
                        <button
                          onClick={() => {
                            handleCopy('mri');
                          }}
                        >
                          <img src={LinkIcon} alt='' /> &nbsp;
                          <span>Copy MRI scan review invitation</span>
                        </button>
                      )}
                      {/* {isScanMember && (
                      <div
                        className='link-container'
                        onClick={() => {
                          handleCopy('mri');
                        }}
                      >
                        <img src={LinkIcon} alt='' />
                        <span>Copy MRI scan review invitation</span>
                      </div>
                    )} */}
                    </div>
                  )}
                </div>

                {/* Misc */}
                {!(hasCompletedOnboarding || (memberData as any)?.no_onboarding === true) && (
                  <div className="submenu-wrapper">
                    <button
                      type="button"
                      onClick={() => toggleSubMenu('misc')}
                    >
                      Misc ▸
                    </button>

                    {openSubMenu === 'misc' && (
                      <div className="submenu">
                        <button
                          onClick={() => {
                            copyOnboardingLink(memberData?.user_id!);
                          }}
                        ><img src={LinkIcon} alt='' /> &nbsp;
                          <span>Copy onboarding link</span></button>

                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='tab-content'>
        {activeTab === 'appointments' && <Appointments />}
        {activeTab === 'summary' && <Summary />}
        {activeTab === 'tests' && <TestResults />}
        {activeTab === 'diet' && <DietDiary />}
        {activeTab === 'health' && <HealthSummaryTab />}
        {activeTab === 'recommendations' && <Recommendations />}
        {activeTab === 'notes' && <Notes noteTab={noteTab} />}
        {activeTab === 'bodySystemAnalysis' && <BodySystemAnalysisTab />}
        {activeTab === 'hl7' && <HL7Listing />}
        {activeTab === 'biologicalAge' && <BiologicalAgeListing />}
        {activeTab === 'supplements' && <Supplements />}
        {activeTab === 'oldBloodTests' && <OldBloodTest />}
        {activeTab === 'dexaReports' && <DexaReports />}
        {activeTab === 'files' && <FilesTab memberId={location.pathname.split('/')[2]} />}
        {activeTab === 'scanQuestionnaires' && <ScanQuestionnaires />}
        {activeTab === 'onboardingForm' && <OnboardingFormData />}
        {activeTab === 'fullBodyScan' && <FullBodyScan />}
        {activeTab === 'personalisedMonitoringPlan' && <PersonalisedMonitoringPlanTab />}
        {activeTab === 'auditLogs' && <AuditLogs />}
        {canUseRestrictedChat && activeTab === 'restrictedChat' && <MemberRestrictedChat />}
      </div>
      {isAssignDoctorModalOpened && (
        <AssignDoctorModal
          isOpen={isAssignDoctorModalOpened}
          onClose={() => {
            dispatch(changeAssignedDoctorId(null));
            dispatch(changeAssignDoctorModalVisibility(false));
          }}
        />
      )}
      {isAssignNutritionistModalOpened && (
        <AssignNutritionistModal
          isOpen={isAssignNutritionistModalOpened}
          onClose={() => {
            dispatch(changeAssignedNutritionistId(null));
            dispatch(changeAssignNutritionistModalVisibility(false));
          }}
        />
      )}
      {isAssignInchargeModalOpened && (
        <AssignInchargeModal
          isOpen={isAssignInchargeModalOpened}
          onClose={() => {
            dispatch(changeAssignedInchargeAdminId(null));
            dispatch(changeAssignInchargeModalVisibility(false));
          }}
        />
      )}
      <ChangeAddressModal
        isOpen={isAddressModalOpened}
        onClose={() => dispatch(changeAddressModalVisibility(false))}
      />
      <MemberMedicalCardInfoModal
        isOpen={isMedicalCardModalOpened}
        onClose={() => dispatch(changeMedicalCardModalVisibility(false))}
      />
      <ViewNoteModal
        isOpen={isViewNoteModalOpen}
        onClose={() => {
          setIsViewNoteModalOpen(false);
          setNoteData(null);
        }}
        noteData={noteData}
        isLoading={false}
        isAlertOpenNote={location.state?.openNoteModal}
        onMarkUnread={onMarkUnread}
      />
      <MemberNotesModal
        isOpen={isNotesModalOpened}
        onClose={() => dispatch(changeNotesModalVisibility(false))}
        forceViewOnly={notesModalForceViewOnly}
        initialMode={notesModalIntent}
        isReferralLetter={notesModalIsReferralLetter}
      />
    </div>
  );
};

export default MemberPage;
